import type { SxProps, Theme } from '@mui/material/styles';
import type { BaseRow, Columns } from '@openx/types/tableHelpers.ts';
import { useMemo } from 'react';
import { TableCell } from '../components/TableCell.tsx';
import type { ColorConfig, ColumnConfig } from '../types.ts';

const createRenderCell = <T extends BaseRow>() => ({
  simple: (
    value: string | number,
    options?: {
      prefix?: string;
      suffix?: string;
      style?: SxProps<Theme>;
      descriptionValue?: string;
      description?: string;
    }
  ) => {
    const finalOptions = getFinalOptions(value, options);
    return (
      <TableCell
        value={value}
        prefix={finalOptions.prefix}
        suffix={finalOptions.suffix}
        style={finalOptions.style}
        descriptionValue={finalOptions.descriptionValue}
        description={finalOptions.description}
      />
    );
  },
  colored: (
    value: string | number,
    rawValue: number,
    config: ColorConfig<T>,
    options?: {
      prefix?: string;
      suffix?: string;
      style?: SxProps<Theme>;
      descriptionValue?: string;
      description?: string;
    }
  ) => {
    const range = config.max - config.min;
    const percent = range === 0 ? 0 : (rawValue - config.min) / range;
    const finalOptions = getFinalOptions(value, options);

    return (
      <TableCell
        value={value}
        percent={percent}
        color={config.color}
        prefix={finalOptions?.prefix}
        suffix={finalOptions?.suffix}
        style={finalOptions?.style}
        descriptionValue={finalOptions?.descriptionValue}
        description={finalOptions?.description}
      />
    );
  },
});

export const useHeatmapTableColumns = <T extends BaseRow>(
  columnsConfig: ColumnConfig<T>[]
): { columns: Columns<T> } => {
  const renderCell = useMemo(() => createRenderCell<T>(), []);

  const columns: Columns<T> = useMemo(
    () =>
      columnsConfig.map(column => ({
        ...column,
        render: (row: T) => {
          if (column.render) {
            return column.render(row);
          }

          const value = row[column.key];
          const stringValue = value === null ? '' : String(value);
          const descriptionValue = column.descriptionKey ? String(row[column.descriptionKey]) : undefined;

          if (column.colorConfig) {
            const rawValue = column.colorConfig.valueKey ? Number(row[column.colorConfig.valueKey]) : Number(value);

            return renderCell.colored(stringValue, rawValue, column.colorConfig, {
              prefix: column.prefix,
              suffix: column.suffix,
              style: column.cellStyle,
              descriptionValue,
              description: column.description,
            });
          }

          return renderCell.simple(stringValue, {
            prefix: column.prefix,
            suffix: column.suffix,
            style: column.cellStyle,
            descriptionValue,
            description: column.description,
          });
        },
      })),
    [columnsConfig, renderCell]
  );

  return { columns };
};

const getFinalOptions = (
  value: string | number,
  options?: {
    prefix?: string;
    suffix?: string;
    style?: SxProps<Theme>;
    descriptionValue?: string;
    description?: string;
  }
) => {
  const isNotAvailable = value === 'N/A';
  return {
    ...options,
    prefix: isNotAvailable ? undefined : options?.prefix,
    suffix: isNotAvailable ? undefined : options?.suffix,
  };
};
